/**
 * Lightweight G-code interpreter and toolpath segment generator.
 * Parses expanded G-code into typed segments with bounding box.
 * No Node.js dependencies — runs entirely in the browser.
 */

import { expandMacros } from './macro-expander'

export interface Vec3 {
  x: number
  y: number
  z: number
}

export type ArcPlane = 'G17' | 'G18' | 'G19'

export interface ToolpathSegment {
  type: 'rapid' | 'cut' | 'arc'
  start: Vec3
  end: Vec3
  center?: Vec3
  clockwise?: boolean
  plane?: ArcPlane     // arc plane (G17=XY, G18=ZX, G19=YZ)
  feedRate: number
  sourceLine: number  // original (pre-expansion) line number, 1-based
  toolNumber: number   // active T word (0 = unknown)
  spindleSpeed: number // active S RPM (0 = unknown)
}

export interface ToolpathStats {
  totalDistance: number       // mm cut+arc travel
  totalRapidDistance: number  // mm rapid travel
  minZ: number
  maxZ: number
  toolNumbers: number[]      // sorted unique tools used
  estimatedCycleTimeSec: number // estimated total cycle time in seconds
}

export interface ToolpathData {
  segments: ToolpathSegment[]
  bounds: { min: Vec3; max: Vec3 }
  totalLines: number
  stats: ToolpathStats
}

/** Format seconds into a human-readable cycle time string */
export function formatCycleTime(seconds: number): string {
  if (seconds <= 0) return '0s'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.round(seconds % 60)
  if (h > 0) return `${h}h ${m}m ${s}s`
  if (m > 0) return `${m}m ${s}s`
  return `${s}s`
}

/**
 * Get the two arc-plane axes and the linear axis for a given plane mode.
 * Returns [u, v, w] accessors where u,v form the arc circle and w is linear.
 *   G17 (XY): u=x, v=y, w=z
 *   G18 (ZX): u=z, v=x, w=y
 *   G19 (YZ): u=y, v=z, w=x
 */
function planeAxes(plane: ArcPlane): {
  getU: (p: Vec3) => number
  getV: (p: Vec3) => number
  getW: (p: Vec3) => number
  build: (u: number, v: number, w: number) => Vec3
} {
  switch (plane) {
    case 'G18':
      return {
        getU: p => p.z, getV: p => p.x, getW: p => p.y,
        build: (u, v, w) => ({ x: v, y: w, z: u }),
      }
    case 'G19':
      return {
        getU: p => p.y, getV: p => p.z, getW: p => p.x,
        build: (u, v, w) => ({ x: w, y: u, z: v }),
      }
    default: // G17
      return {
        getU: p => p.x, getV: p => p.y, getW: p => p.z,
        build: (u, v, w) => ({ x: u, y: v, z: w }),
      }
  }
}

/** Interpolate an arc into polyline points for rendering, respecting the arc plane */
export function interpolateArc(
  start: Vec3,
  end: Vec3,
  center: Vec3,
  clockwise: boolean,
  plane: ArcPlane = 'G17',
  numSegments = 32,
): Vec3[] {
  const points: Vec3[] = [start]
  const ax = planeAxes(plane)

  const su = ax.getU(start), sv = ax.getV(start), sw = ax.getW(start)
  const eu = ax.getU(end),   ev = ax.getV(end),   ew = ax.getW(end)
  const cu = ax.getU(center), cv = ax.getV(center)

  const du1 = su - cu, dv1 = sv - cv
  const du2 = eu - cu, dv2 = ev - cv

  const startAngle = Math.atan2(dv1, du1)
  let endAngle = Math.atan2(dv2, du2)
  const radius = Math.sqrt(du1 * du1 + dv1 * dv1)

  if (clockwise) {
    if (endAngle >= startAngle) endAngle -= 2 * Math.PI
  } else {
    if (endAngle <= startAngle) endAngle += 2 * Math.PI
  }

  const sweep = endAngle - startAngle
  const wDelta = ew - sw

  for (let i = 1; i <= numSegments; i++) {
    const t = i / numSegments
    const angle = startAngle + sweep * t
    points.push(ax.build(
      cu + radius * Math.cos(angle),
      cv + radius * Math.sin(angle),
      sw + wDelta * t,
    ))
  }

  return points
}

/** Parse a word-address G-code line into a map of letter→number pairs */
function parseWords(line: string): Map<string, number> {
  const words = new Map<string, number>()
  // Strip inline comments: (anything) or ; to end
  const clean = line.replace(/\(.*?\)/g, '').replace(/;.*$/, '').trim()
  const regex = /([A-Z])(-?[\d.]+)/gi
  let m: RegExpExecArray | null
  while ((m = regex.exec(clean)) !== null) {
    words.set(m[1].toUpperCase(), parseFloat(m[2]))
  }
  return words
}

export function parseToolpath(gcode: string): ToolpathData {
  const { expanded, lineMap } = expandMacros(gcode)
  const expandedLines = expanded.split('\n')
  const totalLines = gcode.split('\n').length

  const segments: ToolpathSegment[] = []
  const pos: Vec3 = { x: 0, y: 0, z: 0 }
  let motionMode = 'G0'    // G0, G1, G2, G3
  let distanceMode = 'G90' // G90 absolute, G91 relative
  let planeMode: ArcPlane = 'G17'  // G17=XY, G18=ZX, G19=YZ
  let feedRate = 0
  let isMetric = true       // G21 metric (default), G20 imperial
  let toolNumber = 0        // active T word
  let spindleSpeed = 0      // active S RPM

  const in2mm = (v: number) => isMetric ? v : v * 25.4

  for (let i = 0; i < expandedLines.length; i++) {
    const raw = expandedLines[i].trim()
    if (!raw || raw.startsWith(';') || raw.startsWith('(') || raw.startsWith('%')) continue

    const words = parseWords(raw)
    const srcLine = lineMap[i] ?? 0

    // Parse all G codes on the line (handles multi-G lines like "G90 G21 G17")
    const allGCodes: number[] = []
    const gRegex = /G(\d+(?:\.\d+)?)/gi
    let gm: RegExpExecArray | null
    while ((gm = gRegex.exec(raw)) !== null) {
      allGCodes.push(parseFloat(gm[1]))
    }

    let skipLine = false
    for (const gc of allGCodes) {
      if (gc === 0 || gc === 1 || gc === 2 || gc === 3) motionMode = `G${gc}`
      else if (gc === 17) planeMode = 'G17'
      else if (gc === 18) planeMode = 'G18'
      else if (gc === 19) planeMode = 'G19'
      else if (gc === 90) distanceMode = 'G90'
      else if (gc === 91) distanceMode = 'G91'
      else if (gc === 20) isMetric = false
      else if (gc === 21) isMetric = true
      else if (gc === 28 || gc === 80) skipLine = true
    }
    if (skipLine) continue

    // Update feedrate, tool number, spindle speed
    if (words.has('F')) feedRate = in2mm(words.get('F')!)
    if (words.has('T')) toolNumber = words.get('T')!
    if (words.has('S')) spindleSpeed = words.get('S')!

    // Check if this line has any motion (X, Y, Z, I, J, K, R)
    const hasMotion = words.has('X') || words.has('Y') || words.has('Z')
    if (!hasMotion) continue

    const isRelative = distanceMode === 'G91'
    const start: Vec3 = { ...pos }

    const resolveAxis = (letter: string, current: number) => {
      if (!words.has(letter)) return current
      const val = in2mm(words.get(letter)!)
      return isRelative ? current + val : val
    }

    const end: Vec3 = {
      x: resolveAxis('X', pos.x),
      y: resolveAxis('Y', pos.y),
      z: resolveAxis('Z', pos.z),
    }

    if (motionMode === 'G0') {
      segments.push({
        type: 'rapid',
        start,
        end,
        feedRate: 0,
        sourceLine: srcLine,
        toolNumber,
        spindleSpeed,
      })
    } else if (motionMode === 'G1') {
      segments.push({
        type: 'cut',
        start,
        end,
        feedRate,
        sourceLine: srcLine,
        toolNumber,
        spindleSpeed,
      })
    } else if (motionMode === 'G2' || motionMode === 'G3') {
      const clockwise = motionMode === 'G2'
      const ax = planeAxes(planeMode)

      if (words.has('R')) {
        // R-format arc: compute center from radius in the active plane
        const radius = in2mm(words.get('R')!)
        const du = ax.getU(end) - ax.getU(start)
        const dv = ax.getV(end) - ax.getV(start)
        const dist = Math.sqrt(du * du + dv * dv)
        if (dist > 0 && Math.abs(radius) * 2 >= dist) {
          const h = Math.sqrt(Math.max(0, radius * radius - (dist * dist) / 4))
          let sign = clockwise ? -1 : 1
          if (radius < 0) sign = -sign
          const cu = (ax.getU(start) + ax.getU(end)) / 2 - sign * h * dv / dist
          const cv = (ax.getV(start) + ax.getV(end)) / 2 + sign * h * du / dist
          const center = ax.build(cu, cv, ax.getW(start))

          segments.push({
            type: 'arc',
            start,
            end,
            center,
            clockwise,
            plane: planeMode,
            feedRate,
            sourceLine: srcLine,
            toolNumber,
            spindleSpeed,
          })
        }
      } else {
        // IJK-format arc: I offsets X, J offsets Y, K offsets Z (always incremental)
        const ci = words.has('I') ? in2mm(words.get('I')!) : 0
        const cj = words.has('J') ? in2mm(words.get('J')!) : 0
        const ck = words.has('K') ? in2mm(words.get('K')!) : 0
        const center: Vec3 = {
          x: start.x + ci,
          y: start.y + cj,
          z: start.z + ck,
        }
        segments.push({
          type: 'arc',
          start,
          end,
          center,
          clockwise,
          plane: planeMode,
          feedRate,
          sourceLine: srcLine,
          toolNumber,
          spindleSpeed,
        })
      }
    }

    // Update position
    pos.x = end.x
    pos.y = end.y
    pos.z = end.z
  }

  // Calculate bounding box
  const min: Vec3 = { x: Infinity, y: Infinity, z: Infinity }
  const max: Vec3 = { x: -Infinity, y: -Infinity, z: -Infinity }

  for (const seg of segments) {
    for (const pt of [seg.start, seg.end]) {
      min.x = Math.min(min.x, pt.x)
      min.y = Math.min(min.y, pt.y)
      min.z = Math.min(min.z, pt.z)
      max.x = Math.max(max.x, pt.x)
      max.y = Math.max(max.y, pt.y)
      max.z = Math.max(max.z, pt.z)
    }
    if (seg.center) {
      const r = Math.sqrt(
        (seg.start.x - seg.center.x) ** 2 +
        (seg.start.y - seg.center.y) ** 2,
      )
      min.x = Math.min(min.x, seg.center.x - r)
      min.y = Math.min(min.y, seg.center.y - r)
      max.x = Math.max(max.x, seg.center.x + r)
      max.y = Math.max(max.y, seg.center.y + r)
    }
  }

  if (segments.length === 0) {
    min.x = min.y = min.z = 0
    max.x = max.y = max.z = 0
  }

  // Compute stats: distances, Z range, unique tools, cycle time
  const RAPID_RATE = 15000 // mm/min typical CNC rapid traverse
  let totalDistance = 0
  let totalRapidDistance = 0
  let totalTimeSec = 0
  let minZ = Infinity
  let maxZ = -Infinity
  const toolSet = new Set<number>()

  for (const seg of segments) {
    const dx = seg.end.x - seg.start.x
    const dy = seg.end.y - seg.start.y
    const dz = seg.end.z - seg.start.z
    let dist: number

    if (seg.type === 'rapid') {
      dist = Math.sqrt(dx * dx + dy * dy + dz * dz)
      totalRapidDistance += dist
      totalTimeSec += (dist / RAPID_RATE) * 60
    } else if (seg.type === 'cut') {
      dist = Math.sqrt(dx * dx + dy * dy + dz * dz)
      totalDistance += dist
      const rate = seg.feedRate > 0 ? seg.feedRate : RAPID_RATE
      totalTimeSec += (dist / rate) * 60
    } else if (seg.type === 'arc' && seg.center) {
      // Approximate arc length from interpolated points
      dist = 0
      const pts = interpolateArc(seg.start, seg.end, seg.center, seg.clockwise ?? false, seg.plane)
      for (let j = 0; j < pts.length - 1; j++) {
        const ax = pts[j + 1].x - pts[j].x
        const ay = pts[j + 1].y - pts[j].y
        const az = pts[j + 1].z - pts[j].z
        dist += Math.sqrt(ax * ax + ay * ay + az * az)
      }
      totalDistance += dist
      const rate = seg.feedRate > 0 ? seg.feedRate : RAPID_RATE
      totalTimeSec += (dist / rate) * 60
    }

    minZ = Math.min(minZ, seg.start.z, seg.end.z)
    maxZ = Math.max(maxZ, seg.start.z, seg.end.z)
    if (seg.toolNumber > 0) toolSet.add(seg.toolNumber)
  }

  if (segments.length === 0) {
    minZ = 0
    maxZ = 0
  }

  const stats: ToolpathStats = {
    totalDistance,
    totalRapidDistance,
    minZ,
    maxZ,
    toolNumbers: [...toolSet].sort((a, b) => a - b),
    estimatedCycleTimeSec: totalTimeSec,
  }

  return { segments, bounds: { min, max }, totalLines, stats }
}
